import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { SellableUnit, SellableUnitCreate, SellableUnitUpdate } from '@/app/types/sellableUnit';

const sellableUnitSchema = z.object({
  display_name: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be less than 100 characters'),
  quantity_multiplier: z
    .string()
    .min(1, 'Quantity multiplier is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: 'Quantity multiplier must be a positive number',
    })
    .refine((val) => {
      const num = Number(val);
      const decimalPlaces = (val.split('.')[1] || '').length;
      return decimalPlaces <= 5;
    }, {
      message: 'Quantity multiplier can have maximum 5 decimal places',
    }),
  sort_order: z
    .string()
    .refine((val) => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
      message: 'Sort order must be a non-negative number',
    })
    .transform((val) => val === '' ? '0' : val),
  is_active: z.boolean().default(true),
  site_id: z.number().nullable().optional(),
});

type SellableUnitFormData = z.infer<typeof sellableUnitSchema>;

interface SellableUnitFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit?: SellableUnit | null;
  onSubmit: (data: SellableUnitCreate | SellableUnitUpdate) => Promise<void>;
  isSubmitting?: boolean;
}

export function SellableUnitForm({
  open,
  onOpenChange,
  unit,
  onSubmit,
  isSubmitting = false,
}: SellableUnitFormProps) {
  const isEditMode = Boolean(unit);

  const form = useForm<SellableUnitFormData>({
    resolver: zodResolver(sellableUnitSchema),
    defaultValues: {
      display_name: unit?.display_name || '',
      quantity_multiplier: unit?.quantity_multiplier || '',
      sort_order: unit?.sort_order?.toString() || '0',
      is_active: unit?.is_active ?? true,
      site_id: unit?.site_id || null,
    },
  });

  const handleSubmit = async (data: SellableUnitFormData) => {
    const formattedData = {
      display_name: data.display_name,
      quantity_multiplier: Number(data.quantity_multiplier),
      sort_order: Number(data.sort_order),
      is_active: data.is_active,
      site_id: data.site_id || null,
    };

    if (isEditMode) {
      // For update, only send changed fields
      const updateData: SellableUnitUpdate = {};
      if (formattedData.display_name !== unit?.display_name) {
        updateData.display_name = formattedData.display_name;
      }
      if (formattedData.quantity_multiplier !== Number(unit?.quantity_multiplier)) {
        updateData.quantity_multiplier = formattedData.quantity_multiplier;
      }
      if (formattedData.sort_order !== unit?.sort_order) {
        updateData.sort_order = formattedData.sort_order;
      }
      if (formattedData.is_active !== unit?.is_active) {
        updateData.is_active = formattedData.is_active;
      }
      if (formattedData.site_id !== unit?.site_id) {
        updateData.site_id = formattedData.site_id;
      }
      await onSubmit(updateData);
    } else {
      await onSubmit(formattedData as SellableUnitCreate);
    }
    
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Sellable Unit' : 'Add Sellable Unit'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the details of this sellable unit.'
              : 'Create a new sellable unit for this item.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 200g Pouch, Family Pack"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Customer-friendly name shown in mobile apps
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="quantity_multiplier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity Multiplier</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.00001"
                      placeholder="e.g., 0.2 for 200g if base unit is KG"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Multiplier for the base unit quantity (max 5 decimal places)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sort_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Display order (lower numbers appear first)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Enable this sellable unit for use in transactions
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}